/**
 * Vehicle Entry Step - Per-category accordion with real vehicle data
 *
 * Shows an accordion per category from Step 3 (Vehicle Categories).
 * Each vehicle row captures: plate, make/model (catalog), year, km, fuel type.
 * Quick-fill feature to duplicate a model across remaining slots.
 *
 * VL/VU/PL use cascading selects (brand → model) from the vehicle catalog.
 * ENGIN_CHANTIER and MOTO use free-text inputs (no catalog coverage).
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Car, Truck, Container, HardHat, Bike, Plus, Trash2, Check,
  ChevronDown, ChevronUp, Copy, FileSpreadsheet, Search
} from 'lucide-react';
import { vehicleCatalogAPI, truckCatalogAPI } from '../../../../api';
import VehicleImportModal from '../../../../components/vehicles/modals/VehicleImportModal.jsx';

// French license plate format validation (AA-123-BB)
function formatLicensePlate(value) {
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
  return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5, 7)}`;
}

const CATEGORY_META = {
  VL: { label: 'Voitures (VL)', icon: Car, color: 'bg-blue-500', useCatalog: true, catalogType: 'car' },
  VU: { label: 'Camionnettes (VU)', icon: Truck, color: 'bg-green-500', useCatalog: true, catalogType: 'car' },
  PL: { label: 'Camions (PL)', icon: Container, color: 'bg-amber-500', useCatalog: true, catalogType: 'truck' },
  ENGIN_CHANTIER: { label: 'Engins de chantier', icon: HardHat, color: 'bg-purple-500', useCatalog: false },
  MOTO: { label: 'Motos / Deux-roues', icon: Bike, color: 'bg-rose-500', useCatalog: false },
};

const FUEL_TYPES = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'gasoline', label: 'Essence' },
  { value: 'electric', label: 'Électrique' },
  { value: 'hybrid', label: 'Hybride' },
  { value: 'gpl', label: 'GPL' },
];

// ============================================================================
// Brand/Model Select with search (shared cache)
// ============================================================================

const brandsCache = { car: null, truck: null };

function useBrands(catalogType) {
  const [brands, setBrands] = useState(brandsCache[catalogType] || []);
  const [loading, setLoading] = useState(!brandsCache[catalogType]);

  useEffect(() => {
    if (brandsCache[catalogType]) {
      setBrands(brandsCache[catalogType]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const api = catalogType === 'truck' ? truckCatalogAPI : vehicleCatalogAPI;
    api.getBrands({ limit: 500 })
      .then(res => {
        const items = res.ok ? (res.data?.items || res.data || []) : [];
        // Normalize: truck brands have brand_name directly, car brands too
        const normalized = items.map(b => b.brand_name || b.name || '').filter(Boolean).sort();
        const unique = [...new Set(normalized)];
        brandsCache[catalogType] = unique;
        setBrands(unique);
      })
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));
  }, [catalogType]);

  return { brands, loading };
}

function useModels(catalogType, brandName) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandName) { setModels([]); return; }
    setLoading(true);

    const fetchModels = catalogType === 'truck'
      ? truckCatalogAPI.getModels({ brand: brandName, limit: 200 })
      : vehicleCatalogAPI.cascade.getModels(brandName);

    fetchModels
      .then(res => {
        const items = res.ok ? (res.data?.items || res.data || []) : [];
        // Strip brand prefix from model_name (catalog stores "Renault Kangoo" → we want "Kangoo")
        const names = items.map(m => {
          const raw = m.model_name || m.name || '';
          return brandName && raw.toLowerCase().startsWith(brandName.toLowerCase() + ' ')
            ? raw.slice(brandName.length + 1)
            : raw;
        }).filter(Boolean);
        const unique = [...new Set(names)].sort();
        setModels(unique);
      })
      .catch(() => setModels([]))
      .finally(() => setLoading(false));
  }, [catalogType, brandName]);

  return { models, loading };
}

// Searchable select component (lightweight, inline)
function SearchableSelect({ value, options, onChange, placeholder, loading, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const ref = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Check if dropdown should open upward (near bottom edge)
  const handleToggle = useCallback(() => {
    if (!isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 220);
    }
    setIsOpen(!isOpen);
    setSearch('');
  }, [isOpen]);

  const filtered = search
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-left truncate focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
          value ? 'text-gray-900' : 'text-gray-400'
        }`}
      >
        {loading ? '...' : value || placeholder}
        <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400" />
      </button>

      {isOpen && !loading && (
        <div className={`absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-hidden ${
          openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}>
          {/* Search */}
          {options.length > 8 && (
            <div className="p-1.5 border-b border-gray-100">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher..."
                  autoFocus
                  className="w-full pl-7 pr-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-amber-400"
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto max-h-44">
            {/* Clear option */}
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setIsOpen(false); }}
                className="w-full px-3 py-1.5 text-left text-sm text-gray-400 hover:bg-gray-50 italic"
              >
                -- Effacer --
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">Aucun résultat</div>
            ) : (
              filtered.map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => { onChange(opt); setIsOpen(false); }}
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-amber-50 ${
                    opt === value ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {opt}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Vehicle Row
// ============================================================================

function CatalogVehicleRow({ vehicle, index, onUpdate, onDelete, catalogType }) {
  const { brands, loading: brandsLoading } = useBrands(catalogType);
  const { models, loading: modelsLoading } = useModels(catalogType, vehicle.make);

  const handleBrandChange = (brand) => {
    // Reset model when brand changes
    onUpdate(index, { make: brand, model: '' });
  };

  return (
    <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 border border-gray-200 rounded-lg group flex-wrap sm:flex-nowrap">
      <span className="text-gray-400 text-sm font-mono w-6 text-center shrink-0">{index + 1}</span>

      {/* License plate */}
      <input
        type="text"
        value={vehicle.license_plate || ''}
        onChange={(e) => onUpdate(index, { license_plate: formatLicensePlate(e.target.value) })}
        placeholder="AA-123-BB"
        maxLength={9}
        className="w-[100px] px-2 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-center font-mono text-sm uppercase focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0"
      />

      {/* Brand select */}
      <SearchableSelect
        value={vehicle.make || ''}
        options={brands}
        onChange={handleBrandChange}
        placeholder="Marque"
        loading={brandsLoading}
        className="w-[120px] sm:w-[140px] shrink-0"
      />

      {/* Model select */}
      <SearchableSelect
        value={vehicle.model || ''}
        options={models}
        onChange={(model) => onUpdate(index, { model })}
        placeholder={vehicle.make ? 'Modèle' : 'Choisir marque...'}
        loading={modelsLoading}
        className="flex-1 min-w-[80px]"
      />

      {/* Year */}
      <input
        type="number"
        value={vehicle.year || ''}
        onChange={(e) => onUpdate(index, { year: parseInt(e.target.value) || '' })}
        placeholder="Année"
        min={2000}
        max={2030}
        className="w-[68px] px-1 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-sm text-center focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0"
      />

      {/* Fuel type */}
      <select
        value={vehicle.fuel_type || 'diesel'}
        onChange={(e) => onUpdate(index, { fuel_type: e.target.value })}
        className="w-[82px] px-1 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0"
      >
        {FUEL_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      {/* Delete */}
      <button
        onClick={() => onDelete(index)}
        className="p-1 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function FreeTextVehicleRow({ vehicle, index, onUpdate, onDelete, categoryId }) {
  const isEnginChantier = categoryId === 'ENGIN_CHANTIER';

  return (
    <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 border border-gray-200 rounded-lg group flex-wrap sm:flex-nowrap">
      <span className="text-gray-400 text-sm font-mono w-6 text-center shrink-0">{index + 1}</span>

      {/* License plate / Serial number */}
      <input
        type="text"
        value={vehicle.license_plate || ''}
        onChange={(e) => onUpdate(index, { license_plate: isEnginChantier ? e.target.value.toUpperCase() : formatLicensePlate(e.target.value) })}
        placeholder={isEnginChantier ? 'N° de série' : 'AA-123-BB'}
        maxLength={isEnginChantier ? 30 : 9}
        className={`${isEnginChantier ? 'w-[130px]' : 'w-[100px]'} px-2 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-center font-mono text-sm uppercase focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0`}
      />

      {/* Make (free text) */}
      <input
        type="text"
        value={vehicle.make || ''}
        onChange={(e) => onUpdate(index, { make: e.target.value.toUpperCase() })}
        placeholder="Marque"
        className="w-[110px] sm:w-[140px] px-2 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0"
      />

      {/* Model (free text) */}
      <input
        type="text"
        value={vehicle.model || ''}
        onChange={(e) => onUpdate(index, { model: e.target.value })}
        placeholder="Modèle"
        className="flex-1 min-w-[80px] px-2 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
      />

      {/* Year */}
      <input
        type="number"
        value={vehicle.year || ''}
        onChange={(e) => onUpdate(index, { year: parseInt(e.target.value) || '' })}
        placeholder="Année"
        min={2000}
        max={2030}
        className="w-[68px] px-1 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-sm text-center focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0"
      />

      {/* Fuel type */}
      <select
        value={vehicle.fuel_type || 'diesel'}
        onChange={(e) => onUpdate(index, { fuel_type: e.target.value })}
        className="w-[82px] px-1 py-1.5 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent shrink-0"
      >
        {FUEL_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      {/* Delete */}
      <button
        onClick={() => onDelete(index)}
        className="p-1 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Category Accordion
// ============================================================================

function CategoryAccordion({ categoryId, count, vehicles, onVehiclesChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const meta = CATEGORY_META[categoryId] || CATEGORY_META.VL;
  const Icon = meta.icon;

  const categoryVehicles = vehicles.filter(v => v.vehicle_category === categoryId);
  const filledCount = categoryVehicles.filter(v => v.license_plate || v.make).length;

  const handleUpdateVehicle = (localIndex, updates) => {
    let globalIdx = -1;
    let localCount = 0;
    for (let i = 0; i < vehicles.length; i++) {
      if (vehicles[i].vehicle_category === categoryId) {
        if (localCount === localIndex) { globalIdx = i; break; }
        localCount++;
      }
    }
    if (globalIdx === -1) return;

    const updated = [...vehicles];
    updated[globalIdx] = { ...updated[globalIdx], ...updates };
    onVehiclesChange(updated);
  };

  const handleDeleteVehicle = (localIndex) => {
    let globalIdx = -1;
    let localCount = 0;
    for (let i = 0; i < vehicles.length; i++) {
      if (vehicles[i].vehicle_category === categoryId) {
        if (localCount === localIndex) { globalIdx = i; break; }
        localCount++;
      }
    }
    if (globalIdx === -1) return;

    const updated = vehicles.filter((_, i) => i !== globalIdx);
    onVehiclesChange(updated);
  };

  const handleAddVehicle = () => {
    onVehiclesChange([...vehicles, {
      vehicle_category: categoryId,
      license_plate: '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      fuel_type: 'diesel',
      mileage: 0,
    }]);
    if (!isOpen) setIsOpen(true);
  };

  // Quick-fill: duplicate first filled vehicle for all empty slots
  const handleQuickFill = () => {
    const firstFilled = categoryVehicles.find(v => v.make);
    if (!firstFilled) return;

    const updated = vehicles.map(v => {
      if (v.vehicle_category === categoryId && !v.make && !v.license_plate) {
        return {
          ...v,
          make: firstFilled.make,
          model: firstFilled.model,
          year: firstFilled.year,
          fuel_type: firstFilled.fuel_type,
        };
      }
      return v;
    });
    onVehiclesChange(updated);
  };

  const hasFilledVehicle = categoryVehicles.some(v => v.make);
  const hasEmptyVehicles = categoryVehicles.some(v => !v.make && !v.license_plate);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-visible">
      {/* Accordion header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors"
      >
        <div className={`w-10 h-10 ${meta.color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="text-gray-900 font-medium">{meta.label} ({count})</h3>
          <p className="text-gray-400 text-sm">
            {filledCount}/{count} renseigné{filledCount > 1 ? 's' : ''}
          </p>
        </div>
        {filledCount === count && count > 0 ? (
          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-green-600" />
          </div>
        ) : null}
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {/* Accordion content */}
      {isOpen && (
        <div className="p-4 pt-0 space-y-2 border-t border-gray-100 overflow-visible">
          {/* Column headers */}
          {categoryVehicles.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 text-xs text-gray-400 font-medium">
              <span className="w-6">#</span>
              <span className={categoryId === 'ENGIN_CHANTIER' ? 'w-[130px]' : 'w-[100px]'}>
                {categoryId === 'ENGIN_CHANTIER' ? 'N° série' : 'Plaque'}
              </span>
              <span className="w-[120px] sm:w-[140px]">Marque</span>
              <span className="flex-1">Modèle</span>
              <span className="w-[68px] text-center">Année</span>
              <span className="w-[82px]">Carburant</span>
              <span className="w-6"></span>
            </div>
          )}

          {/* Vehicle rows */}
          {categoryVehicles.map((vehicle, localIndex) => (
            meta.useCatalog ? (
              <CatalogVehicleRow
                key={localIndex}
                vehicle={vehicle}
                index={localIndex}
                onUpdate={handleUpdateVehicle}
                onDelete={handleDeleteVehicle}
                catalogType={meta.catalogType}
              />
            ) : (
              <FreeTextVehicleRow
                key={localIndex}
                vehicle={vehicle}
                index={localIndex}
                onUpdate={handleUpdateVehicle}
                onDelete={handleDeleteVehicle}
                categoryId={categoryId}
              />
            )
          ))}

          {/* Actions row */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleAddVehicle}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>

            {hasFilledVehicle && hasEmptyVehicles && (
              <button
                onClick={handleQuickFill}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Dupliquer le premier modèle sur les lignes vides"
              >
                <Copy className="w-4 h-4" />
                Dupliquer le modèle
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Step Component
// ============================================================================

export function VehicleEntryStep({ data, updateData }) {
  const categories = data.vehicleCategories || {};
  const vehicles = data.vehicles || [];

  // Ensure vehicle slots exist for each category count
  const ensuredVehicles = useMemo(() => {
    const result = [...vehicles];
    for (const [catId, count] of Object.entries(categories)) {
      if (count <= 0) continue;
      const existing = result.filter(v => v.vehicle_category === catId).length;
      for (let i = existing; i < count; i++) {
        result.push({
          vehicle_category: catId,
          license_plate: '',
          make: '',
          model: '',
          year: new Date().getFullYear(),
          fuel_type: 'diesel',
          mileage: 0,
        });
      }
    }
    return result;
  }, []);

  // Excel import modal
  const [showImportModal, setShowImportModal] = useState(false);

  // Initialize vehicles on first render if empty
  const [initialized, setInitialized] = useState(false);
  if (!initialized && ensuredVehicles.length > vehicles.length) {
    updateData({ vehicles: ensuredVehicles });
    setInitialized(true);
  }

  const handleVehiclesChange = useCallback((newVehicles) => {
    updateData({ vehicles: newVehicles });
  }, [updateData]);

  // Map the modal's category codes (which come from Excel 'genre' detection)
  // to the wizard's category keys. 'VP' (voiture particulière) ≡ 'VL' here.
  const normalizeCategory = (cat) => {
    if (!cat) return 'VL';
    const c = String(cat).toUpperCase();
    if (c === 'VP') return 'VL';
    if (c === 'ENGIN') return 'ENGIN_CHANTIER';
    if (['VL', 'VU', 'PL', 'ENGIN_CHANTIER', 'MOTO'].includes(c)) return c;
    return 'VL';
  };

  // Merge imported Excel rows into the wizard state:
  //   - fill empty slots of the matching category first
  //   - append new slots when the category quota isn't high enough
  //   - updateData is called once with both `vehicles` and updated
  //     `vehicleCategories` counts so the accordions reflect the new rows.
  const handleImportSuccess = useCallback((parsedVehicles) => {
    if (!Array.isArray(parsedVehicles) || parsedVehicles.length === 0) {
      setShowImportModal(false);
      return;
    }

    const current = [...(data.vehicles || [])];
    const newCategoryCounts = { ...(data.vehicleCategories || {}) };

    for (const imported of parsedVehicles) {
      const cat = normalizeCategory(imported.vehicle_category);
      const slotIdx = current.findIndex(
        v => v.vehicle_category === cat && !v.license_plate && !v.make
      );
      const row = {
        vehicle_category: cat,
        license_plate: imported.license_plate || '',
        make: imported.make && imported.make !== 'Unknown' ? imported.make : '',
        model: imported.model && imported.model !== 'Unknown' ? imported.model : '',
        year: imported.year || new Date().getFullYear(),
        fuel_type: imported.fuel_type || 'diesel',
        mileage: 0,
        // Keep anything else the import modal captured (VIN, power, insurance…)
        vin: imported.vin,
        power_hp: imported.power_hp,
        seats: imported.seats,
        _insurance: imported._insurance,
      };
      if (slotIdx >= 0) {
        current[slotIdx] = { ...current[slotIdx], ...row };
      } else {
        current.push(row);
        newCategoryCounts[cat] = (newCategoryCounts[cat] || 0) + 1;
      }
    }

    updateData({ vehicles: current, vehicleCategories: newCategoryCounts });
    setShowImportModal(false);
  }, [data.vehicles, data.vehicleCategories, updateData]);

  // Active categories (count > 0)
  const activeCategories = Object.entries(categories)
    .filter(([_, count]) => count > 0)
    .sort((a, b) => {
      const order = ['VL', 'VU', 'PL', 'ENGIN_CHANTIER', 'MOTO'];
      return order.indexOf(a[0]) - order.indexOf(b[0]);
    });

  const totalVehicles = activeCategories.reduce((sum, [_, count]) => sum + count, 0);
  const filledVehicles = vehicles.filter(v => v.license_plate || v.make).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">
          Détails de vos véhicules
        </h2>
        <p className="text-gray-500">
          Renseignez au minimum la plaque (ou le n° de série pour les engins) pour chaque véhicule
        </p>
      </div>

      {/* Stats pill */}
      {totalVehicles > 0 && (
        <div className="flex items-center justify-center">
          <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-sm">
            <span className="text-amber-700 font-medium">{filledVehicles}/{totalVehicles}</span>
            <span className="text-amber-600 ml-1">véhicule{totalVehicles > 1 ? 's' : ''} renseigné{filledVehicles > 1 ? 's' : ''}</span>
          </div>
        </div>
      )}

      {/* Category accordions */}
      {activeCategories.length > 0 ? (
        <div className="space-y-3">
          {activeCategories.map(([catId, count]) => (
            <CategoryAccordion
              key={catId}
              categoryId={catId}
              count={count}
              vehicles={vehicles}
              onVehiclesChange={handleVehiclesChange}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-400">
            Retournez à l'étape précédente pour indiquer vos catégories de véhicules
          </p>
        </div>
      )}

      {/* Excel import hint */}
      <div className="text-center">
        <button
          onClick={() => setShowImportModal(true)}
          className="text-gray-400 hover:text-amber-500 text-sm flex items-center gap-2 mx-auto transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Importer depuis un fichier Excel
        </button>
      </div>

      {/* Skip hint */}
      <div className="text-center">
        <p className="text-gray-400 text-sm">
          Vous pourrez compléter les détails plus tard depuis le module Parc
        </p>
      </div>

      <VehicleImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleImportSuccess}
        onMessage={(msg) => console.log('[VehicleImport]', msg)}
        skipApi={true}
      />
    </div>
  );
}

export default VehicleEntryStep;
